// Words and number phrases for Sales › Weather. Every number arrives computed
// by the backend (engine/weather/) and becomes text only through formatters.ts.
import { fmt, fmtChangeFixed, fmtCount, fmtPct, fmtSigned } from './formatters';

export type ConditionKey = 'rain_morning' | 'snow' | 'hot' | 'cold';
export type GroupKey = 'store' | 'morning' | 'hot_drinks' | 'iced_drinks' | 'food';
export type CallKind = 'slower' | 'busier' | 'normal' | 'holiday' | 'unknown';

/** What a forecast day's chances would do to one group: fractions, chance-weighted. */
export interface Shift { condition: string; group: string; change: number; low: number; high: number }

export interface OutlookDay {
  date: string;
  weekday: string;
  horizon: number;
  label: 'likely' | 'possible';
  call: CallKind;
  notable: boolean;
  /** A usual day for this weekday, in dollars. */
  usual?: number;
  /** The store's expected change as a fraction (−0.16), with its range. */
  change?: number;
  low?: number;
  high?: number;
  dollars?: number;
  dollars_low?: number;
  dollars_high?: number;
  conditions?: { condition: string; chance: number; pattern: boolean }[];
  shifts?: Shift[];
  weather?: { high_f: number; low_f: number; rain_am_chance: number };
  // The rebuild's additions (all optional: an older payload lacks them).
  low_f?: number;
  snow_chance?: number;
  usual_basis?: { weekday: string; weeks: number } | null;
  /** The shop is closed that day (Ruling, 2026-10-26: not in the contract's list). */
  closed?: boolean;
}

/** One clean condition day: x is the day against its usual (−0.17 = 17% below). */
export interface PatternPoint { date: string; x: number; actual: number; usual: number }
/** Ordinary days' x, as a spread. */
export interface OrdinaryBand { p10: number; q1: number; median: number; q3: number; p90: number; n: number }
export interface PatternExample { date: string; actual: number; usual: number }
/** Median revenue per local hour (index 0 = midnight). */
export interface HourlyProfile { ordinary: number[]; condition: number[]; n_condition: number; n_ordinary: number }

export interface Pattern {
  condition: string;
  group: string;
  status: string;
  effect: number;
  low: number;
  high: number;
  n_days: number;
  n_ordinary?: number;
  typical_day?: number;
  dollars_low?: number;
  dollars_high?: number;
  window: { start: string; end: string };
  holdout: { window_start: string; window_end: string; status?: string; passed?: boolean };
  points?: PatternPoint[];
  ordinary?: OrdinaryBand;
  examples?: PatternExample[];
  hourly?: HourlyProfile | null;
}

/** A condition and group still at a gate. Older payloads carry only condition, status, text (store). */
export interface NotYet {
  condition: string;
  group?: string;
  status: string;
  text: string;
  n_days?: number;
  needed?: number;
  n_ordinary?: number;
  needed_ordinary?: number;
  /** The window a failed holdout was tested on, when the backend sends it. */
  holdout?: { window_start: string; window_end: string };
}

export interface YearDay {
  date: string;
  open: boolean;
  actual: number | null;
  usual: number | null;
  x: number | null;
  condition: string | null;
  holiday: boolean;
}

export interface RecordCall {
  for_date: string;
  made_on?: string;
  call: 'slower' | 'busier';
  condition: string;
  hit: boolean | null;
  weather_happened: boolean | null;
  scored: boolean;
  usual: number;
  dollars: number;
  dollars_low: number;
  dollars_high: number;
  actual: number | null;
}

export interface WeatherRecord {
  hits: number;
  total: number;
  since?: string;
  by_condition: { condition: string; hits: number; total: number }[];
  recent: any[];
  calls?: RecordCall[];
}

/**
 * One "What to do" line as the backend sends it (engine/weather/outlook.py
 * build_actions): numbers only. low and high are the shown pattern's range for
 * `group`, as signed percents (−21.74 is 21.74% below a usual day). A general
 * line has no for_date or weekday. fraction is the prep size in words, or null
 * when the day's cautious end is under 10%. actionText builds the sentence.
 */
export interface WeatherAction {
  for_date: string | null;
  condition: string;
  group: string;
  low: number;
  high: number;
  weekday: string | null;
  fraction: string | null;
  fewer_hot: boolean;
}

export interface WeatherPayload {
  status: string;
  location: { label: string; sample: boolean; cell_id?: string };
  data_through: string;
  computed_on: string;
  problems: string[];
  patterns: Pattern[];
  not_yet: NotYet[];
  outlook: OutlookDay[];
  record: WeatherRecord;
  note: { explanation?: string; week_ahead_note?: string; by?: string; written_on?: string } | null;
  guesses: { text: string; made_on: string; status: string }[];
  year?: YearDay[];
  actions?: WeatherAction[];
}

export const CONDITION_NAMES: Record<string, string> = {
  rain_morning: 'Rainy mornings', snow: 'Snow days', hot: 'Hot days', cold: 'Cold days',
};
export const GROUP_NAMES: Record<string, string> = {
  store: 'Whole day', morning: 'Morning rush (6–11am)', hot_drinks: 'Hot drinks',
  iced_drinks: 'Iced drinks', food: 'Food',
};
const CAUSE: Record<string, string> = {
  rain_morning: 'rain in the morning rush', snow: 'snow', hot: 'heat', cold: 'cold',
};
export const GUESS_STATUS: Record<string, string> = {
  open: 'Being checked', held_up: 'Held up', did_not_hold_up: "Didn't hold up", unclear: 'Unclear so far',
};

/** "Mon, Oct 12", read at noon so a YYYY-MM-DD stays on its own day. */
export const dayLabel = (iso: string): string =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

/** A pattern's range, both ends signed, one decimal: "−11.2% to −6.0%". */
export const rangeText = (low: number, high: number): string => `${fmtChangeFixed(low * 100)} to ${fmtChangeFixed(high * 100)}`;

/** Two dollar amounts as one range, smaller first: "$64–$180". */
export const moneyRange = (a: number, b: number): string => {
  const [lo, hi] = [Math.abs(a), Math.abs(b)].sort((x, y) => x - y);
  return `${fmt(lo)}–${fmt(hi)}`;
};

export function callWords(d: OutlookDay): string {
  if (d.call === 'holiday') return 'Holiday, no weather call';
  if (d.call === 'unknown') return 'No forecast yet';
  const cause = (c: { condition: string }) => CAUSE[c.condition] ?? c.condition;
  const conditions = d.conditions || [];
  // Mirrors engine/weather/service.py _call_words (math audit 2026-10-13):
  // "no pattern yet" only for conditions without one; a slower or busier day
  // names the patterned conditions that moved it.
  if (d.call === 'normal') {
    const unshown = conditions.filter((c) => !c.pattern).map(cause).join(' and ');
    return unshown ? `A usual day (${unshown}: no pattern yet)` : 'A usual day';
  }
  const why = conditions.filter((c) => c.pattern).map(cause).join(' and ');
  const word = d.call === 'slower' ? 'Slower' : 'Busier';
  return why ? `${word}: ${why}` : word;
}

export function dollarsLine(d: OutlookDay): string {
  if (d.call !== 'slower' && d.call !== 'busier') return '';
  // A range from below a usual day to above it has no one direction to print.
  if ((d.dollars_low ?? 0) < 0 && (d.dollars_high ?? 0) > 0) return '';
  return `${moneyRange(d.dollars_low ?? 0, d.dollars_high ?? 0)} ${d.call === 'slower' ? 'below' : 'above'} a usual ${d.weekday}`;
}

export const recordHeadline = (hits: number, total: number): string =>
  total ? `Right ${fmtCount(hits)} of ${fmtCount(total)}` : 'No calls scored yet';

export interface ShopLocation { address?: string; label?: string; geocode?: string; source?: string }

/** One line under the address field saying what ClearPath made of it. */
export function describeLocation(loc?: ShopLocation | null): string {
  if (!loc || !loc.address) return '';
  if (loc.geocode === 'matched' || loc.geocode === 'from_source') {
    return `Placed in ${loc.label || loc.address}${loc.source === 'square' ? ' · from Square' : ''}`;
  }
  if (loc.geocode === 'no_match') return "We couldn't find that address. Check the street and city.";
  return "We couldn't place that address yet. We'll try again tomorrow morning.";
}

// ---- The rebuild (2026-10-23) ----

export type GlyphKind = 'rain' | 'snow' | 'sun' | 'cold' | 'cloud' | 'none';

export const CONDITION_ORDER: ConditionKey[] = ['rain_morning', 'snow', 'hot', 'cold'];
export const GROUP_ORDER: GroupKey[] = ['store', 'morning', 'hot_drinks', 'iced_drinks', 'food'];
/** Short names for a row or a chip: "Snow: 3 of 8 snowy days needed". */
export const CONDITION_SHORT: Record<string, string> = {
  rain_morning: 'Rain in the rush', snow: 'Snow', hot: 'Heat', cold: 'Cold',
};
/** The days a condition counts, plural: "18 rainy mornings". */
export const CONDITION_DAYS: Record<string, string> = {
  rain_morning: 'rainy mornings', snow: 'snowy days', hot: 'hot days', cold: 'cold days',
};
/** One such day, for "Last rainy rush: ...". */
export const CONDITION_ONE: Record<string, string> = {
  rain_morning: 'rainy rush', snow: 'snow day', hot: 'hot day', cold: 'cold day',
};

export const conditionGlyph = (condition: string): GlyphKind =>
  ({ rain_morning: 'rain', snow: 'snow', hot: 'sun', cold: 'cold' } as Record<string, GlyphKind>)[condition] ?? 'cloud';

/** The one glyph a day wears: the weather that matters most to the shop. A closed day wears none. */
export function dayGlyph(d: OutlookDay): GlyphKind | null {
  if (d.closed) return null;
  if (d.call === 'unknown' || !d.weather) return 'none';
  const chance = (c: string) => (d.conditions || []).find((x) => x.condition === c)?.chance ?? 0;
  if ((d.snow_chance ?? 0) >= 0.5 || chance('snow') >= 0.5) return 'snow';
  if (d.weather.rain_am_chance >= 0.5 || chance('rain_morning') >= 0.5) return 'rain';
  if (chance('cold') >= 0.5) return 'cold';
  if (chance('hot') >= 0.5) return 'sun';
  return d.weather.rain_am_chance >= 0.3 ? 'cloud' : 'sun';
}

/** The day's low: the contract's top-level low_f, else the weather block's. */
export const lowF = (d: OutlookDay): number | undefined => d.low_f ?? d.weather?.low_f;

/** Two dollar amounts in words, smaller first: "$227 to $270". */
export const moneyRangeWords = (a: number, b: number): string => {
  const [lo, hi] = [Math.abs(a), Math.abs(b)].sort((x, y) => x - y);
  return `${fmt(lo)} to ${fmt(hi)}`;
};

/** Two dollar amounts as one signed range, smaller first: "−$189 to −$276". */
export const signedMoneyRange = (a: number, b: number): string => {
  const [lo, hi] = [a, b].sort((x, y) => Math.abs(x) - Math.abs(y));
  return `${fmtSigned(lo)} to ${fmtSigned(hi)}`;
};

const moved = (d: OutlookDay) => d.call === 'slower' || d.call === 'busier';

/** One dollar scale for the whole week: the biggest end of any moved day (1 when none moved). */
export function dollarScale(days: OutlookDay[]): number {
  const ends = days.filter(moved).flatMap((d) => [Math.abs(d.dollars_low ?? 0), Math.abs(d.dollars_high ?? 0), Math.abs(d.dollars ?? 0)]);
  return Math.max(1, ...ends);
}

/** The next open day, and whether a closed tomorrow was skipped to reach it. */
export function tomorrowDay(days: OutlookDay[]): { day: OutlookDay; skipped: boolean } | null {
  const i = days.findIndex((d) => !d.closed);
  return i < 0 ? null : { day: days[i], skipped: i > 0 };
}

export interface ConditionCard { condition: string; lead: Pattern; patterns: Pattern[]; notYet: NotYet[] }

/**
 * Cards for every condition with a shown pattern (lead: store, else morning,
 * else the shown category with the largest effect, fix round 1), and one progress row (its store gate) for each
 * condition with none shown. Ruling 2026-10-26: cold shown only for hot drinks
 * still gets a card, so a shown pattern is never hidden.
 */
export function splitConditions(patterns: Pattern[], notYet: NotYet[]): { cards: ConditionCard[]; progress: NotYet[] } {
  const rank = (g: string) => { const i = GROUP_ORDER.indexOf(g as GroupKey); return i < 0 ? 99 : i; };
  const known = CONDITION_ORDER as string[];
  const extra = Array.from(new Set(patterns.map((p) => p.condition).concat(notYet.map((n) => n.condition))))
    .filter((c) => !known.includes(c));
  const cards: ConditionCard[] = [];
  const progress: NotYet[] = [];
  known.concat(extra).forEach((condition) => {
    // Lead: the whole day, then the morning, then the category that moves most.
    const leadRank = (g: string) => Math.min(rank(g), 2);
    const shown = patterns.filter((p) => p.condition === condition && p.status === 'shown')
      .sort((a, b) => leadRank(a.group) - leadRank(b.group) || Math.abs(b.effect) - Math.abs(a.effect) || rank(a.group) - rank(b.group));
    const gated = notYet.filter((n) => n.condition === condition)
      .sort((a, b) => rank(a.group ?? 'store') - rank(b.group ?? 'store'));
    if (shown.length) cards.push({ condition, lead: shown[0], patterns: shown, notYet: gated });
    else if (gated.length) progress.push({ ...gated[0], group: gated[0].group ?? 'store' });
  });
  return { cards, progress };
}

/** The glyph's name when it stands alone. */
export const GLYPH_TITLE: Record<GlyphKind, string> = {
  rain: 'Rain in the morning rush', snow: 'Snow', sun: 'Clear', cold: 'Cold', cloud: 'Clouds', none: 'No forecast',
};

/** The word on a day's chip. A closed day says so before anything else. */
export const callChip = (d: OutlookDay): string =>
  d.closed ? 'Closed'
    : ({ slower: 'Slower', busier: 'Busier', normal: 'Usual', holiday: 'Holiday', unknown: 'No forecast' } as Record<CallKind, string>)[d.call];

const sentenceCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** A moved day's dollars cross a usual day (the range runs from below to above it). */
const straddles = (d: OutlookDay) => (d.dollars_low ?? 0) < 0 && (d.dollars_high ?? 0) > 0;

/** The Tomorrow card's big figure: the dollar range, or a word when there is no number. */
export function tomorrowFigure(d: OutlookDay): string {
  if (d.call === 'holiday') return 'Holiday';
  if (d.call === 'unknown' || d.closed) return 'No forecast';
  if (d.call === 'normal') return 'About usual';
  if (straddles(d)) return `About ${fmtSigned(d.dollars ?? 0)}`;
  return signedMoneyRange(d.dollars_low ?? 0, d.dollars_high ?? 0);
}

/**
 * The Tomorrow card's one sentence. "likely" only on days 1 to 3 (the day's own
 * label); `when` is "tomorrow" or "on Sunday" when a closed day was skipped.
 */
export function tomorrowSentence(d: OutlookDay, when: string): string {
  if (d.call === 'holiday') return `${sentenceCase(when)} is a holiday. Holidays run their own way, so there is no weather call.`;
  if (d.call === 'unknown' || d.closed) return `No forecast for ${when.replace(/^on /, '')} yet, so no call.`;
  if (d.call === 'normal') return `Nothing in the forecast moves your sales ${when}.`;
  const causes = (d.conditions || []).filter((c) => c.pattern).map((c) => CAUSE[c.condition] ?? c.condition);
  const word = d.call === 'slower' ? 'slower' : 'busier';
  const pcts = [d.low ?? d.change ?? 0, d.high ?? d.change ?? 0].map((v) => Math.abs(v * 100)).sort((a, b) => a - b);
  const amount = ((d.low ?? 0) < 0 && (d.high ?? 0) > 0) || pcts[0] === pcts[1]
    ? fmtPct(Math.abs((d.change ?? 0) * 100))
    : `${fmtPct(pcts[0])} to ${fmtPct(pcts[1])}`;
  const expect = `Expect about ${amount} ${word} than a usual ${d.weekday}.`;
  if (!causes.length) return expect;
  return `${sentenceCase(causes.join(' and '))} ${causes.length > 1 ? 'are' : 'is'} ${d.label}. ${expect}`;
}

/** One sentence for a screen reader standing in for the week strip. */
export function weekSentence(days: OutlookDay[]): string {
  const parts: string[] = [];
  let quiet = 0;
  days.forEach((d) => {
    if (d.closed) parts.push(`${d.weekday} closed`);
    else if (d.call === 'holiday') parts.push(`${d.weekday} a holiday`);
    else if (d.call === 'unknown') parts.push(`${d.weekday} no forecast`);
    else if (d.call === 'slower' || d.call === 'busier') {
      const range = straddles(d) ? '' : `, about ${moneyRangeWords(d.dollars_low ?? 0, d.dollars_high ?? 0)} ${d.call === 'slower' ? 'below' : 'above'} a usual day`;
      parts.push(`${d.weekday} ${d.label === 'possible' ? 'possibly ' : ''}${d.call}${range}`);
    } else quiet += 1;
  });
  if (quiet) parts.push(parts.length ? 'the other days usual' : 'every day usual');
  return `The week ahead: ${parts.join('; ')}.`;
}

/** Short group names for a bar's label. */
export const GROUP_SHORT: Record<string, string> = {
  store: 'Whole day', morning: 'Morning rush', hot_drinks: 'Hot drinks', iced_drinks: 'Iced drinks', food: 'Food',
};

export function median(values: number[]): number {
  if (!values.length) return 0;
  const s = values.slice().sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

/** A day's x in words: "6.6% below", "5.7% above", "at its usual". */
export const aboveBelow = (x: number): string => {
  const words = fmtPct(Math.abs(x * 100));
  if (words === '0%') return 'at its usual';
  return `${words} ${x < 0 ? 'below' : 'above'}`;
};

/** A pattern's series name: "Rainy mornings", or "Cold days, hot drinks" for a group. */
export const seriesName = (condition: string, group = 'store'): string => {
  const name = CONDITION_NAMES[condition] ?? condition;
  return group === 'store' ? name : `${name}, ${(GROUP_SHORT[group] ?? group).toLowerCase()}`;
};

/** The dot plot's one-sentence summary. */
export function dotPlotSentence(condition: string, points: PatternPoint[], ordinary: OrdinaryBand, group = 'store'): string {
  const name = seriesName(condition, group);
  const mid = median(points.map((p) => p.x));
  return `${name}: ${fmtCount(points.length)} days ran a median ${aboveBelow(mid)} their usual. `
    + `Ordinary days mostly ran from ${aboveBelow(ordinary.p10)} to ${aboveBelow(ordinary.p90)}.`;
}

/** A gated group's quiet line, counts only, never an effect: "Food: not enough rainy mornings yet (5 of 8)". */
export function gateLine(n: NotYet): string {
  const name = GROUP_SHORT[n.group ?? 'store'] ?? n.group;
  const days = CONDITION_DAYS[n.condition] ?? 'days like this';
  const counts = n.n_days != null && n.needed != null ? ` (${fmtCount(n.n_days)} of ${fmtCount(n.needed)})` : '';
  switch (n.status) {
    case 'not_enough_days': return `${name}: not enough ${days} yet${counts}`;
    case 'not_checked_yet': return `${name}: not enough recent ${days} to check yet`;
    case 'failed_holdout': return `${name}: no pattern that holds up yet`;
    case 'no_clear_effect': return `${name}: no clear change yet`;
    default: return `${name}: not shown yet`;
  }
}

// ---- The record (Task 5) ----

export type CallOutcome = 'right' | 'missed' | 'never' | 'open';

/** How a call came out: right, missed, the weather never came, or not scored yet. */
export function callOutcome(c: RecordCall): CallOutcome {
  if (!c.scored) return 'open';
  if (c.weather_happened === false) return 'never';
  if (c.hit === true) return 'right';
  if (c.hit === false) return 'missed';
  return 'never';
}

export const OUTCOME_WORDS: Record<CallOutcome, string> = {
  right: 'right', missed: 'missed', never: 'the weather never came', open: 'not scored yet',
};

/** "Oct 13": a date short enough for a chart row. */
export const shortDay = (iso: string): string =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/** Calls in date order, oldest first. */
export const byDate = <T extends { for_date: string }>(calls: T[]): T[] =>
  calls.slice().sort((a, b) => (a.for_date < b.for_date ? -1 : a.for_date > b.for_date ? 1 : 0));

/** Count of each outcome. */
export function outcomeCounts(calls: RecordCall[]): Record<CallOutcome, number> {
  const out: Record<CallOutcome, number> = { right: 0, missed: 0, never: 0, open: 0 };
  calls.forEach((c) => { out[callOutcome(c)] += 1; });
  return out;
}

/** The expected-against-actual chart's one sentence. */
export function callsSentence(calls: RecordCall[]): string {
  const n = outcomeCounts(calls);
  const parts = [`${fmtCount(n.right)} right`, `${fmtCount(n.missed)} missed`];
  if (n.never) parts.push(`${fmtCount(n.never)} where the weather never came`);
  const list = parts.length > 2 ? `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}` : parts.join(' and ');
  return `Each scored call against the range it expected: ${list}.`;
}

// ---- The year at this shop (Task 6) ----

const DAY = 86_400_000;
const utcDay = (iso: string) => Date.parse(`${iso}T00:00:00Z`);
const isoOf = (t: number) => new Date(t).toISOString().slice(0, 10);

/**
 * The trailing 52 weeks as columns of seven days, Monday first. The last column
 * holds the newest day; a slot with no day (still to come, or outside the
 * window) is null.
 */
export function yearWeeks(year: YearDay[]): (YearDay | null)[][] {
  if (!year.length) return [];
  const byDate = new Map(year.map((d) => [d.date, d]));
  const last = utcDay(year.reduce((a, d) => (d.date > a ? d.date : a), year[0].date));
  const lastMonday = last - ((new Date(last).getUTCDay() + 6) % 7) * DAY;
  const firstMonday = lastMonday - 51 * 7 * DAY;
  return Array.from({ length: 52 }, (_, w) => Array.from({ length: 7 }, (__, r) =>
    byDate.get(isoOf(firstMonday + (w * 7 + r) * DAY)) ?? null));
}

/** A day's x in five steps: 10% or more below, 3 to 10% below, near usual, and the same above. */
export const heatStep = (x: number): number => {
  const a = Math.abs(x);
  const size = a >= 0.1 ? 2 : a >= 0.03 ? 1 : 0;
  return x < 0 ? -size : size;
};

/** A measured open day (not a holiday, with a usual to compare to). */
const measured = (d: YearDay) => d.open && !d.holiday && d.x != null;

/** How one weather's days did: "Rainy mornings: 16 of 18 ran below their usual, a median 16.4% below." */
export function conditionYearSentence(year: YearDay[], condition: string): string {
  const days = year.filter((d) => measured(d) && d.condition === condition);
  const name = CONDITION_NAMES[condition] ?? condition;
  if (!days.length) return `${name}: none in the last year.`;
  const xs = days.map((d) => d.x as number);
  const below = xs.filter((x) => x < 0).length;
  return `${name}: ${fmtCount(below)} of ${fmtCount(days.length)} ran below their usual, a median ${aboveBelow(median(xs))}.`;
}

/** The heatmap's one sentence. */
export function yearSentence(year: YearDay[]): string {
  const xs = year.filter(measured).map((d) => d.x as number);
  const below = xs.filter((x) => heatStep(x) < 0).length;
  const above = xs.filter((x) => heatStep(x) > 0).length;
  const conditions = CONDITION_ORDER.filter((c) => year.some((d) => measured(d) && d.condition === c));
  return `The last 52 weeks, one square a day: ${fmtCount(below)} open days ran 3% or more below their usual, `
    + `${fmtCount(above)} ran 3% or more above, and ${fmtCount(xs.length - below - above)} sat near it.`
    + conditions.map((c) => ` ${conditionYearSentence(year, c)}`).join('');
}

export interface MonthTotal { month: string; label: string; open: number; sold: number; usual: number; measuredSold: number; weatherDays: number }

/** Month by month: open days, what they sold, and the measured days against their usual. */
export function monthTotals(year: YearDay[]): MonthTotal[] {
  const out = new Map<string, MonthTotal>();
  year.forEach((d) => {
    const month = d.date.slice(0, 7);
    const t = out.get(month) ?? {
      month, open: 0, sold: 0, usual: 0, measuredSold: 0, weatherDays: 0,
      label: new Date(`${month}-15T12:00:00`).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
    };
    if (d.open) t.open += 1;
    t.sold += d.actual ?? 0;
    if (measured(d) && d.usual != null && d.actual != null) { t.usual += d.usual; t.measuredSold += d.actual; }
    if (d.condition) t.weatherDays += 1;
    out.set(month, t);
  });
  return Array.from(out.values()).sort((a, b) => (a.month < b.month ? -1 : 1));
}

// ---- What to do, and Claude's words (Task 7) ----

/** A standing action's weather, for its chip: "On a rainy morning". */
export const CONDITION_WHEN: Record<string, string> = {
  rain_morning: 'On a rainy morning', snow: 'On a snowy day', hot: 'On a hot day', cold: 'On a cold day',
};

/** What a guess's check means, in a sentence. */
export const GUESS_CHECK: Record<string, string> = {
  open: 'Code is checking this against your sales as the days come in.',
  held_up: 'Your sales so far agree with it.',
  did_not_hold_up: "Your sales so far don't agree with it.",
  unclear: 'Not enough days yet to say either way.',
};

// ---- The page (Task 8) ----

/** Sales are stale when the newest day is more than 7 days before today. */
export function salesStale(dataThrough: string, today: Date = new Date()): boolean {
  const local = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return local - utcDay(dataThrough) > 7 * DAY;
}

/** The short problem codes the backend sends, and what the page says for each. */
export const PROBLEM_WORDS: Record<string, string> = {
  forecast: 'The forecast could not be reached this morning.',
  history: 'Past weather could not be reached this morning, so the patterns use the weather already on file.',
};

/**
 * The payload's problems, read: known codes become their sentence (and the
 * forecast flag); any plain sentence shows as it is; an unknown bare code
 * (snake_case, no spaces) is never printed.
 */
export function readProblems(problems: string[] | undefined): { forecast: boolean; notices: string[] } {
  const list = problems || [];
  const notices = list.map((p) => PROBLEM_WORDS[p] ?? (/^[a-z0-9_]+$/i.test(p.trim()) ? '' : p.trim())).filter(Boolean);
  return { forecast: list.includes('forecast'), notices: Array.from(new Set(notices)) };
}

/** Seven empty days from the morning's date, for a week with no forecast. */
export function forecastDownDays(computedOn: string): OutlookDay[] {
  return Array.from({ length: 7 }, (_, i) => {
    const date = isoOf(utcDay(computedOn) + (i + 1) * DAY);
    return {
      date, weekday: new Date(`${date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long' }), horizon: i + 1,
      label: i < 3 ? 'likely' : 'possible', call: 'unknown', notable: false,
    } as OutlookDay;
  });
}

/**
 * Dev only: where to read the page from instead of the API. "?fixture=1" is the
 * bundled sample; "?fixture=<file>" a JSON file in the dev server's public
 * folder. The /dev/weather route reads the bundled sample by default. Always
 * null in a production build.
 */
export function fixtureSource(search: string, env: string | undefined = process.env.NODE_ENV, pathname = ''): string | null {
  if (env === 'production') return null;
  const value = new URLSearchParams(search).get('fixture');
  if (value == null || value === '') return pathname.startsWith('/dev/') ? 'bundled' : null;
  if (value === '1') return 'bundled';
  // Same origin, a plain file path only.
  if (/^[\w./-]+$/.test(value) && !value.includes('..') && !value.includes('//')) return `/${value.replace(/^\/+/, '')}`;
  return null;
}

/** After a usual tomorrow, the next open day the weather moves (slower or busier). */
export const nextMoveDay = (days: OutlookDay[], shown: OutlookDay): OutlookDay | null =>
  days.slice(days.indexOf(shown) + 1).find((d) => !d.closed && (d.call === 'slower' || d.call === 'busier')) ?? null;

/** The next move's range: "−$189 to −$276", or "about −$40" when it crosses a usual day. */
export const moveAmount = (d: OutlookDay): string =>
  (straddles(d) ? `about ${fmtSigned(d.dollars ?? 0)}` : signedMoneyRange(d.dollars_low ?? 0, d.dollars_high ?? 0));

/** Under a usual tomorrow: the next day the weather moves, or that none does. */
export function nextMoveLine(days: OutlookDay[], shown: OutlookDay): string | null {
  if (shown.call !== 'normal') return null;
  const after = nextMoveDay(days, shown);
  if (!after) return 'Nothing else this week moves your sales either.';
  const verb = after.label === 'possible' ? 'may be' : 'looks';
  return `Next: ${after.weekday} ${verb} ${after.call}, ${moveAmount(after)} against a usual ${after.weekday}.`;
}

// ---- What to do about it (review 2026-10-26) ----

/** A group as an action line says it: "On rainy mornings, the morning rush runs ...". */
const ACTION_GROUP: Record<string, string> = {
  store: 'the whole day runs', morning: 'the morning rush runs', hot_drinks: 'hot drinks run',
  iced_drinks: 'iced drinks run', food: 'food runs',
};
const ACTION_WHEN: Record<string, string> = {
  rain_morning: 'On rainy mornings', snow: 'On snow days', hot: 'On hot days', cold: 'On cold days',
};

/** The pattern behind an action, naming its group, with the range as the cards print it. */
const actionRange = (a: WeatherAction): string =>
  `${ACTION_WHEN[a.condition] ?? CONDITION_NAMES[a.condition] ?? a.condition}, `
  + `${ACTION_GROUP[a.group] ?? `${(GROUP_SHORT[a.group] ?? a.group).toLowerCase()} runs`} `
  + `${fmtChangeFixed(a.low)} to ${fmtChangeFixed(a.high)} against a usual day.`;

const GENERAL_ADVICE: Record<string, string> = {
  rain_morning: 'Keep the 6 to 11 prep lighter when rain is in the forecast.',
  snow: 'Plan a lighter day when snow is in the forecast.',
  hot: 'Prep more iced drinks when heat is in the forecast.',
  cold: 'Expect more hot drinks when cold is in the forecast.',
};

/** One "What to do" sentence, from the backend's numbers. */
export function actionText(a: WeatherAction): string {
  const range = actionRange(a);
  if (!a.for_date || !a.weekday) return GENERAL_ADVICE[a.condition] ? `${range} ${GENERAL_ADVICE[a.condition]}` : range;
  const day = a.weekday;
  switch (a.condition) {
    case 'rain_morning':
      return a.fraction ? `Pull ${day}'s 6 to 11 bake back about ${a.fraction}. ${range}` : range;
    case 'snow':
      return `Plan a lighter ${day}. ${range}`;
    case 'hot':
      return `Prep for ${a.fraction ? `about ${a.fraction} more` : 'more'} iced drinks ${day}${a.fewer_hot ? ', and fewer hot ones' : ''}.`;
    case 'cold':
      return `Expect more hot drinks ${day}. ${range}`;
    default:
      return range;
  }
}
